// Email service utility for sending quote requests
import emailjs from 'emailjs-com';

// EmailJS Configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_m00r2de';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_jygsnkj';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'QTUUW7rZpX_5uJkrF';

export const sendQuoteRequest = async (formData, language = 'en') => {
  try {
    // Handle image attachment conversion
    let imageData = '';
    let imageName = '';
    
    if (formData.referenceImage) {
      try {
        // Compress and convert image to base64 for EmailJS
        const base64 = await compressAndConvertImage(formData.referenceImage, 40);
        imageData = base64;
        imageName = formData.referenceImage.name;
      } catch (imageError) {
        console.warn('Failed to process image, sending without attachment:', imageError);
      }
    }

    // Prepare template parameters for EmailJS
    const templateParams = {
      to_email: 'nina@sweetlikenina.com',
      to_name: 'Nina',
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone || 'Not provided',
      cake_type: formData.cakeType || 'Not specified',
      cake_size: formData.size || 'Not specified',
      description: formData.description,
      language: language,
      timestamp: new Date().toLocaleString('en-GB', { 
        timeZone: 'Europe/London',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      subject: `New Cake Quote Request from ${formData.name}`,
      // Image attachment data
      attachment_data: imageData,
      attachment_name: imageName,
      has_image: formData.referenceImage ? 'Yes' : 'No',
      // Additional formatted message for email body
      message: `
New Cake Quote Request:

Customer Details:
- Name: ${formData.name}
- Email: ${formData.email}
- Phone: ${formData.phone || 'Not provided'}

Cake Details:
- Type: ${formData.cakeType || 'Not specified'}
- Size: ${formData.size || 'Not specified'}

Description:
${formData.description}

Reference Image: ${formData.referenceImage ? `Attached (${formData.referenceImage.name})` : 'No image provided'}

Submitted on: ${new Date().toLocaleString('en-GB', { 
        timeZone: 'Europe/London'
      })}

Please respond to the customer at: ${formData.email}
      `.trim()
    };

    console.log('Sending email with params:', { ...templateParams, attachment_data: imageData ? '[IMAGE DATA]' : 'No image' });

    // Send email using EmailJS
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );

    console.log('Email sent successfully:', response);
    return {
      success: true,
      message: language === 'en' 
        ? 'Quote request sent successfully! We\'ll get back to you within 24 hours.' 
        : 'Cererea de ofertă a fost trimisă cu succes! Vă vom contacta în 24 de ore.'
    };

  } catch (error) {
    console.error('Email send error:', error);
    return {
      success: false,
      message: language === 'en' 
        ? 'Failed to send request. Please try calling us directly at 07751 712390.' 
        : 'Eroare la trimiterea cererii. Vă rugăm să ne sunați la 07751 712390.'
    };
  }
};

// Helper function to compress and convert image to base64
const compressAndConvertImage = (file, maxSizeKB = 40) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions (max 800px width/height)
      let { width, height } = img;
      const maxDimension = 800;
      
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Try different quality levels to get under size limit
      let quality = 0.8;
      let base64;
      
      do {
        base64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
        const sizeKB = (base64.length * 3) / 4 / 1024; // Approximate KB size
        
        if (sizeKB <= maxSizeKB) {
          resolve(base64);
          return;
        }
        
        quality -= 0.1;
      } while (quality > 0.1);
      
      // If still too large, resolve with smaller version
      resolve(canvas.toDataURL('image/jpeg', 0.1).split(',')[1]);
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

// Validation utilities
export const validateQuoteForm = (formData, language = 'en') => {
  const errors = {};
  
  // Required fields validation
  if (!formData.name || formData.name.trim().length < 2) {
    errors.name = language === 'en' 
      ? 'Name must be at least 2 characters' 
      : 'Numele trebuie să aibă cel puțin 2 caractere';
  }
  
  if (!formData.email || !isValidEmail(formData.email)) {
    errors.email = language === 'en' 
      ? 'Please enter a valid email address' 
      : 'Vă rugăm să introduceți o adresă de email validă';
  }
  
  if (!formData.phone || formData.phone.trim().length < 7) {
    errors.phone = language === 'en' 
      ? 'Phone number is required' 
      : 'Numărul de telefon este obligatoriu';
  }
  
  if (!formData.description || formData.description.trim().length < 10) {
    errors.description = language === 'en' 
      ? 'Please provide a detailed description (at least 10 characters)' 
      : 'Vă rugăm să furnizați o descriere detaliată (cel puțin 10 caractere)';
  }
  
  if (!formData.size) {
    errors.size = language === 'en' 
      ? 'Please select a cake size' 
      : 'Vă rugăm să selectați mărimea tortului';
  }
  
  // Phone validation (if provided, should be valid format)
  if (formData.phone && !isValidPhone(formData.phone)) {
    errors.phone = language === 'en' 
      ? 'Please enter a valid phone number' 
      : 'Vă rugăm să introduceți un număr de telefon valid';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Helper functions
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidPhone = (phone) => {
  // Accepts UK phone numbers and international formats
  const phoneRegex = /^[+]?[0-9\s\-()]{7,}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

// Image upload utility (for reference images)
export const uploadReferenceImage = async (file) => {
  try {
    console.log('Uploading file:', file.name);
    
    // For now, return a placeholder implementation
    // TODO: Implement actual image upload to Firebase Storage if needed
    return {
      success: true,
      url: URL.createObjectURL(file), // Temporary local URL
      filename: file.name
    };
    
  } catch (error) {
    console.error('Image upload error:', error);
    return {
      success: false,
      error: 'Failed to upload image'
    };
  }
};